/**
 * ChatAgentPanel Component
 * Pannello laterale per collegare la chat a un Lead/Cliente (e progetto) e
 * impostare l'assistente AI. Solo impostazioni: i suggerimenti sono nel
 * pannello laterale (UnifiedRightPanel).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Select from 'react-select';
import {
  X, Bot, Users, Zap, ListTodo, Loader2, Brain,
} from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useClientStore } from '@/lib/stores/clientStore';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { useApp } from '@/components/AppProvider';
import { iconTip } from '@/components/messages/ChatTooltip';
import type { AgentResponseMode } from '@/lib/types/whatsappAgent';

interface ChatAgentPanelProps {
  chatId: string;
  chatName: string;
  isGroup: boolean;
  isOpen: boolean;
  onClose: () => void;
}

interface ProjectOption {
  id: string;
  name: string;
  client_id: string | null;
}

const MODE_CONFIG: Record<AgentResponseMode, { label: string; color: string; bgColor: string }> = {
  off: { label: 'Spento', color: 'text-gray-700 dark:text-gray-200', bgColor: 'bg-gray-100 dark:bg-gray-800' },
  suggest: { label: 'Suggerisce', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/30' },
  auto: { label: 'Risponde da solo', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/30' },
};

export function ChatAgentPanel({
  chatId,
  chatName,
  isGroup,
  isOpen,
  onClose,
}: ChatAgentPanelProps) {
  const { isDark } = useTheme();
  const { confirm } = useApp();
  const { wahaConfig } = useWhatsAppChatStore();
  const {
    assignments,
    fetchAssignments,
    createAssignment,
    updateAssignment,
  } = useWhatsAppAgentStore();
  const { selectableClients, loading: clientsLoading, fetchClientsForSelect } = useClientStore();

  const [allProjects, setAllProjects] = useState<ProjectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Local state for form
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [responseMode, setResponseMode] = useState<AgentResponseMode>('off');
  const [autoCreateTasks, setAutoCreateTasks] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [includeProjectBrief, setIncludeProjectBrief] = useState(true);
  const [includeStatusUpdates, setIncludeStatusUpdates] = useState(true);
  const [includeTasks, setIncludeTasks] = useState(true);
  const [historyMessages, setHistoryMessages] = useState(20);
  const instructionsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const organizationId = wahaConfig?.organizationId;
  const configId = wahaConfig?.configId;

  // Find existing assignment for this chat
  const assignment = assignments.find(a => a.chat_jid === chatId);
  const isAgentActive = responseMode !== 'off';

  // Notion-style react-select styles
  const notionSelectStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '32px',
      background: 'transparent',
      borderColor: state.isFocused ? '#2383E2' : 'transparent',
      borderRadius: '4px',
      boxShadow: state.isFocused ? '0 0 0 1px rgba(35, 131, 226, 0.3)' : 'none',
      '&:hover': {
        background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(55, 53, 47, 0.03)',
        borderColor: state.isFocused ? '#2383E2' : 'rgba(55, 53, 47, 0.16)'
      }
    }),
    menu: (base: any) => ({
      ...base,
      background: isDark ? '#2d2d2d' : 'white',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(55, 53, 47, 0.1)',
      borderRadius: '6px',
      overflow: 'hidden',
      zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
    }),
    menuList: (base: any) => ({
      ...base,
      padding: '4px',
      maxHeight: '200px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      background: state.isFocused
        ? (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(55, 53, 47, 0.04)')
        : 'transparent',
      color: isDark ? 'white' : '#37352F',
      padding: '6px 10px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px'
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? 'white' : '#37352F',
      fontSize: '13px'
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.35)' : '#9B9A97',
      fontSize: '13px'
    }),
    input: (base: any) => ({
      ...base,
      color: isDark ? 'white' : '#37352F',
      fontSize: '13px'
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.3)' : '#9B9A97',
      padding: '4px',
      '&:hover': { color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#37352F' }
    }),
    clearIndicator: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.3)' : '#9B9A97',
      padding: '4px',
      '&:hover': { color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#37352F' }
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 6px'
    })
  }), [isDark]);

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && organizationId) {
      loadData();
    }
  }, [isOpen, organizationId, chatId]);

  // Sync local state with assignment
  useEffect(() => {
    if (assignment) {
      setSelectedClientId(assignment.client_id || '');
      setSelectedProjectId(assignment.project_id || '');
      setResponseMode(assignment.agent_response_mode || 'off');
      setAutoCreateTasks(assignment.agent_auto_create_tasks || false);
      setCustomInstructions(assignment.agent_custom_instructions || '');
      setIncludeProjectBrief(assignment.agent_include_project_brief !== false);
      setIncludeStatusUpdates(assignment.agent_include_status_updates !== false);
      setIncludeTasks(assignment.agent_include_tasks !== false);
      setHistoryMessages(assignment.agent_history_messages ?? 20);
    } else {
      setSelectedClientId('');
      setSelectedProjectId('');
      setResponseMode('off');
      setAutoCreateTasks(false);
      setCustomInstructions('');
      setIncludeProjectBrief(true);
      setIncludeStatusUpdates(true);
      setIncludeTasks(true);
      setHistoryMessages(20);
    }
  }, [assignment]);

  const loadData = async () => {
    if (!organizationId) return;
    setLoading(true);
    setProjectsLoading(true);
    try {
      await Promise.all([
        fetchAssignments(organizationId),
        fetchClientsForSelect(),
      ]);

      const { data: projectData } = await supabase
        .from('projects')
        .select('id, name, client_id')
        .eq('organization_id', organizationId)
        .eq('archived', false)
        .order('name');

      setAllProjects(projectData || []);
    } catch (err) {
      console.error('[ChatAgentPanel] Error loading data:', err);
    } finally {
      setLoading(false);
      setProjectsLoading(false);
    }
  };

  // Filter projects by selected client
  const filteredProjects = useMemo(() => {
    if (!selectedClientId) return [];
    return allProjects.filter(p => p.client_id === selectedClientId);
  }, [allProjects, selectedClientId]);

  // Client options for react-select
  const clientOptions = useMemo(() =>
    selectableClients.map(c => ({ value: c.id, label: c.business_name })),
    [selectableClients]
  );

  // Project options for react-select
  const projectOptions = useMemo(() =>
    filteredProjects.map(p => ({ value: p.id, label: p.name })),
    [filteredProjects]
  );

  // --- Handlers ---

  const handleSave = useCallback(async (updates: {
    client_id?: string | null;
    project_id?: string | null;
    agent_response_mode?: AgentResponseMode;
    agent_auto_create_tasks?: boolean;
    agent_custom_instructions?: string | null;
    agent_include_project_brief?: boolean;
    agent_include_status_updates?: boolean;
    agent_include_tasks?: boolean;
    agent_history_messages?: number;
  }) => {
    if (!organizationId || !configId) return;
    setSaving(true);
    try {
      if (assignment) {
        await updateAssignment(assignment.id, {
          ...updates,
          agent_enabled: (updates.agent_response_mode || responseMode) !== 'off',
        });
      } else {
        await createAssignment({
          organization_id: organizationId,
          config_id: configId,
          chat_jid: chatId,
          chat_name: chatName,
          chat_type: isGroup ? 'group' : 'individual',
          client_id: updates.client_id || undefined,
          project_id: updates.project_id || undefined,
          agent_response_mode: updates.agent_response_mode || 'off',
          agent_auto_create_tasks: updates.agent_auto_create_tasks || false,
          agent_enabled: (updates.agent_response_mode || 'off') !== 'off',
        });
        await fetchAssignments(organizationId);
      }
    } catch (err) {
      console.error('[ChatAgentPanel] Error saving:', err);
    } finally {
      setSaving(false);
    }
  }, [organizationId, configId, assignment, chatId, chatName, isGroup, responseMode, updateAssignment, createAssignment, fetchAssignments]);

  const handleClientChange = (selected: any) => {
    const clientId = selected?.value || '';
    setSelectedClientId(clientId);
    setSelectedProjectId('');
    handleSave({ client_id: clientId || null, project_id: null });
  };

  const handleProjectChange = (selected: any) => {
    const projectId = selected?.value || '';
    setSelectedProjectId(projectId);
    handleSave({ project_id: projectId || null });
  };

  const handleModeChange = async (mode: AgentResponseMode) => {
    if (mode === responseMode) return;
    // Le risposte automatiche arrivano al cliente senza passare da un collega:
    // si accendono solo dopo aver spiegato cosa succede.
    if (mode === 'auto') {
      const ok = await confirm({
        title: "Far rispondere l'assistente da solo?",
        message: `Da adesso l'assistente AI scriverà direttamente a ${chatName || 'questo contatto'}, senza chiedere conferma a te o ai colleghi. I messaggi partono subito e il cliente li riceve come se li avessi scritti tu. Puoi spegnerlo in qualsiasi momento da qui.`,
        confirmText: 'Sì, risponde da solo',
        cancelText: 'Annulla',
        type: 'warning',
      });
      if (!ok) return;
    }
    setResponseMode(mode);
    handleSave({ agent_response_mode: mode });
  };

  const handleAutoTasksChange = (enabled: boolean) => {
    setAutoCreateTasks(enabled);
    handleSave({ agent_auto_create_tasks: enabled });
  };

  const handleCustomInstructionsChange = (value: string) => {
    setCustomInstructions(value);
    if (instructionsTimerRef.current) clearTimeout(instructionsTimerRef.current);
    instructionsTimerRef.current = setTimeout(() => {
      handleSave({ agent_custom_instructions: value || null });
    }, 1000);
  };

  const handleHistoryMessagesChange = (value: number) => {
    const clamped = Math.min(Math.max(value, 0), 50);
    setHistoryMessages(clamped);
    if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    historyTimerRef.current = setTimeout(() => {
      handleSave({ agent_history_messages: clamped });
    }, 1000);
  };

  const handleContextToggle = (field: 'agent_include_project_brief' | 'agent_include_status_updates' | 'agent_include_tasks', value: boolean) => {
    if (field === 'agent_include_project_brief') setIncludeProjectBrief(value);
    if (field === 'agent_include_status_updates') setIncludeStatusUpdates(value);
    if (field === 'agent_include_tasks') setIncludeTasks(value);
    handleSave({ [field]: value });
  };

  if (!isOpen) return null;

  const selectedClientOption = clientOptions.find(o => o.value === selectedClientId) || null;
  const selectedProjectOption = projectOptions.find(o => o.value === selectedProjectId) || null;

  return (
    <div className="w-80 h-full bg-white dark:bg-gray-900 border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#F7F7F5] dark:bg-gray-900 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
          {...iconTip('Chiudi impostazioni')}
        >
          <X className="w-5 h-5 text-[#37352F] dark:text-white" />
        </button>
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-green-600" />
          <h3 className="font-semibold text-[#37352F] dark:text-white">
            Impostazioni assistente
          </h3>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-blue-500 ml-auto" />}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2383E2] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">

          {/* ===== ASSOCIAZIONE CRM ===== */}
          <div className="p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#6F6E69] dark:text-white/60" />
              <h4 className="text-xs text-[#6F6E69] dark:text-white/60 font-medium uppercase tracking-wide">
                Collegamento al CRM
              </h4>
            </div>

            {/* Client dropdown */}
            <div className="mb-3">
              <label className="block text-xs text-[#6F6E69] dark:text-white/60 mb-1">Lead o Cliente</label>
              <Select
                isLoading={clientsLoading}
                options={clientOptions}
                value={selectedClientOption}
                onChange={handleClientChange}
                placeholder="Cerca un Lead o un Cliente..."
                aria-label="Lead o Cliente collegato alla chat"
                isClearable
                styles={notionSelectStyles}
                noOptionsMessage={() => 'Nessun Lead o Cliente trovato'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            {/* Project dropdown */}
            <div>
              <label className="block text-xs text-[#6F6E69] dark:text-white/60 mb-1">Progetto</label>
              <Select
                isLoading={projectsLoading}
                options={projectOptions}
                value={selectedProjectOption}
                onChange={handleProjectChange}
                placeholder={selectedClientId ? 'Cerca progetto (facoltativo)...' : 'Prima scegli il Lead o Cliente'}
                aria-label="Progetto collegato alla chat"
                isDisabled={!selectedClientId}
                isClearable
                styles={notionSelectStyles}
                noOptionsMessage={() => 'Nessun progetto per questo Lead/Cliente'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>

          {/* ===== MODALITA' AGENT ===== */}
          <div className="p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-[#6F6E69] dark:text-white/60" />
              <h4 className="text-xs text-[#6F6E69] dark:text-white/60 font-medium uppercase tracking-wide">
                Cosa fa l'assistente
              </h4>
            </div>

            {/* 3-way selector */}
            <div className="flex rounded-lg overflow-hidden border border-[rgba(55,53,47,0.16)] dark:border-white/20">
              {(['off', 'suggest', 'auto'] as AgentResponseMode[]).map((mode) => {
                const config = MODE_CONFIG[mode];
                const isActive = responseMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => { void handleModeChange(mode); }}
                    aria-pressed={isActive}
                    className={`flex-1 py-2 text-xs font-medium transition-colors ${
                      isActive
                        ? `${config.bgColor} ${config.color}`
                        : 'bg-white dark:bg-gray-800 text-[#6F6E69] dark:text-white/60 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-[#6F6E69] dark:text-white/60 mt-2">
              {responseMode === 'off' && "L'assistente non fa niente in questa chat"}
              {responseMode === 'suggest' && "L'assistente prepara le risposte: decidi tu se inviarle, modificarle o scartarle"}
              {responseMode === 'auto' && "L'assistente risponde da solo al cliente, senza chiedere conferma"}
            </p>
          </div>

          {/* ===== AUTO CREATE TASKS ===== */}
          {isAgentActive && (
            <div className="p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListTodo className="w-4 h-4 text-[#6F6E69] dark:text-white/60" />
                  <div>
                    <p className="text-sm text-[#37352F] dark:text-white">Crea i task da solo</p>
                    <p className="text-xs text-[#6F6E69] dark:text-white/60">Un task nel progetto per ogni richiesta del cliente</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoCreateTasks}
                    onChange={(e) => handleAutoTasksChange(e.target.checked)}
                    aria-label="Crea i task da solo"
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}

          {/* ===== CONTESTO AI ===== */}
          {isAgentActive && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-4 h-4 text-[#6F6E69] dark:text-white/60" />
                <h4 className="text-xs text-[#6F6E69] dark:text-white/60 font-medium uppercase tracking-wide">
                  Cosa sa l'assistente
                </h4>
              </div>

              {/* Custom instructions */}
              <div className="mb-3">
                <label className="block text-xs text-[#6F6E69] dark:text-white/60 mb-1">
                  Indicazioni per questa chat
                </label>
                <textarea
                  value={customInstructions}
                  onChange={(e) => handleCustomInstructionsChange(e.target.value)}
                  placeholder="Es: Il cliente chiede spesso aggiornamenti sulla landing page. Rispondi con tono formale..."
                  rows={3}
                  className="w-full px-3 py-2 text-xs bg-transparent border border-[rgba(55,53,47,0.16)] dark:border-white/10 rounded-md text-[#37352F] dark:text-white placeholder:text-[#9B9A97] dark:placeholder:text-white/35 resize-y focus:outline-none focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2]/20 transition-colors"
                />
              </div>

              {/* Context toggles */}
              <label className="block text-xs text-[#6F6E69] dark:text-white/60 mb-2">
                Dati del progetto che può leggere
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeProjectBrief}
                    onChange={(e) => handleContextToggle('agent_include_project_brief', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                  />
                  <span className="text-xs text-[#37352F] dark:text-white">Brief del progetto</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeStatusUpdates}
                    onChange={(e) => handleContextToggle('agent_include_status_updates', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                  />
                  <span className="text-xs text-[#37352F] dark:text-white">Aggiornamenti di stato</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTasks}
                    onChange={(e) => handleContextToggle('agent_include_tasks', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                  />
                  <span className="text-xs text-[#37352F] dark:text-white">Task e avanzamento</span>
                </label>
              </div>

              {/* History messages count */}
              <div className="mt-4 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
                <label className="block text-xs text-[#6F6E69] dark:text-white/60 mb-1">
                  Quanti messaggi rilegge
                </label>
                <p className="text-[11px] text-[#6F6E69] dark:text-white/60 mb-2">
                  Gli ultimi messaggi, tuoi e del cliente, che legge prima di rispondere
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={5}
                    value={historyMessages}
                    onChange={(e) => handleHistoryMessagesChange(Number(e.target.value))}
                    aria-label="Quanti messaggi rilegge"
                    className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full appearance-none cursor-pointer accent-blue-600"
                  />
                  <span className="text-xs font-medium text-[#37352F] dark:text-white min-w-[28px] text-right">
                    {historyMessages === 0 ? 'Nessuno' : historyMessages}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
